// Dependencies
import { FC } from "react";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useMutation } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";

// Components
import { Form, Formik } from "formik";
import { Grid, Typography } from "@mui/material";
import Button from "~/components/Common/Button";
import AuthTitle from "~/components/Auth/AuthTitle";
import FormErrorMessage from "~/components/Common/FormErrorMessage";
import FormPasswordInput from "~/components/Auth/FormPasswordInput";
import PasswordFormLayout from "~/components/Auth/PasswordFormLayout";

// Routes
import config from "~/config";

// api
import { resetPassword } from "~/services/auth";

// Types
import { TQueryError } from "~/types";
import { formValidate } from "~/enums";

// Styles
import { StyledInputWrapper } from "./ResetPasswordForm.styled";

interface ResetPasswordFormProps {}

const ResetPasswordForm: FC<ResetPasswordFormProps> = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // get token in url
  const search = useLocation().search;
  const resetPasswordToken =
    new URLSearchParams(search).get("reset_token") || "";

  const resetPasswordMutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      enqueueSnackbar("Reset password success! Please login again!", {
        variant: "success",
      });
      navigate(config.routes.login);
    },
    onError: (error: TQueryError) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const handleSubmitForm = (values: { password: string }) => {
    resetPasswordMutation.mutate({
      password: values.password,
      resetToken: resetPasswordToken,
    });
  };

  return (
    <Formik
      initialValues={{
        password: "",
        passwordConfirm: "",
      }}
      // validate form values
      validationSchema={Yup.object({
        password: Yup.string()
          .required("Password is required")
          .matches(formValidate.password.regex, formValidate.password.message),
        passwordConfirm: Yup.string()
          .required("Confirmed password is required")
          .oneOf([Yup.ref("password")], "Password must be match"),
      })}
      // handle submit form
      onSubmit={handleSubmitForm}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => (
        <Form>
          <PasswordFormLayout>
            <Grid item>
              <AuthTitle>Change your password!</AuthTitle>
              <Typography variant={"h4"}>
                You must change your password and login again!
              </Typography>
            </Grid>
            <StyledInputWrapper>
              <FormPasswordInput
                label="Mật khẩu"
                name="password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
                autoComplete="false"
              />
              <FormErrorMessage
                hasError={!!(errors.password && touched.password)}
                error={errors.password}
              />

              <FormPasswordInput
                label="Xác nhận mật khẩu"
                name="passwordConfirm"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.passwordConfirm}
                autoComplete="false"
              />
              <FormErrorMessage
                hasError={!!(errors.passwordConfirm && touched.passwordConfirm)}
                error={errors.passwordConfirm}
              />
            </StyledInputWrapper>

            <Button
              variant={"contained"}
              type={"submit"}
              loading={resetPasswordMutation.isLoading}
            >
              Change password
            </Button>
          </PasswordFormLayout>
        </Form>
      )}
    </Formik>
  );
};

export default ResetPasswordForm;
